import { useState, useMemo } from "react";
import { Zap, User, GitCommit, ZoomIn, ZoomOut, Maximize2 } from "lucide-react";

/**
 * Horizontal proposal timeline ribbon.
 * Props:
 *   versions: array (newest-first from API)
 *   currentTs: optional ISO ts for "now" marker (defaults to project.updated_at or now)
 *   onSelect: (version) => void — click a marker to open diff
 *   selectedId: currently highlighted version id
 */
export default function Timeline({ versions = [], currentTs, onSelect, selectedId }) {
  const [hoverId, setHoverId] = useState(null);
  const [zoom, setZoom] = useState(1); // 1x = fit-to-container, higher = more spread out
  const ZOOM_MIN = 1;
  const ZOOM_MAX = 6;

  // Oldest → newest for left-to-right chronological reading
  const items = useMemo(() => [...versions].reverse(), [versions]);

  // Auto-suggest zoom based on version count so 20+ items don't cram
  const suggestedZoom = useMemo(() => {
    if (items.length <= 6) return 1;
    if (items.length <= 12) return 2;
    if (items.length <= 24) return 3;
    return Math.min(ZOOM_MAX, Math.ceil(items.length / 8));
  }, [items.length]);
  const effectiveZoom = Math.max(zoom, suggestedZoom > zoom ? 1 : zoom); // user zoom overrides suggestion after change

  const zoomIn = () => setZoom((z) => Math.min(ZOOM_MAX, z + 1));
  const zoomOut = () => setZoom((z) => Math.max(ZOOM_MIN, z - 1));
  const zoomReset = () => setZoom(1);

  // Evenly-spaced positioning (git-log style). Better UX than timestamp-proportional
  // because it avoids clustering when revisions happen in bursts.
  const positioned = useMemo(() => {
    if (!items.length) return [];
    // Reserve 6% right edge for "Now" marker. Distribute nodes across 4%..90%.
    const start = 0.04;
    const end = 0.9;
    const n = items.length;
    return items.map((v, i) => {
      const p = n === 1 ? (start + end) / 2 : start + (i / (n - 1)) * (end - start);
      return { ...v, _pos: p, _idx: i };
    });
  }, [items]);

  const nowPos = 0.96;

  if (!items.length) {
    return (
      <div className="border bg-card p-6 text-center" data-testid="timeline-empty">
        <GitCommit className="w-6 h-6 mx-auto text-muted-foreground" />
        <div className="text-sm text-muted-foreground mt-2">
          No versions yet — the timeline will populate as revisions accumulate.
        </div>
      </div>
    );
  }

  return (
    <div className="border bg-card" data-testid="timeline-ribbon">
      <div className="px-4 py-2 border-b bg-muted/30 flex items-center justify-between gap-3 flex-wrap">
        <div className="eyebrow flex items-center gap-2">
          <GitCommit className="w-3 h-3" /> Proposal Timeline
          <span className="text-muted-foreground normal-case tracking-normal font-normal">· {items.length} version{items.length === 1 ? "" : "s"}</span>
        </div>
        <div className="flex items-center gap-4 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-[var(--peak-blue)]" /> AI revision</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-black" /> Manual snapshot</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 border border-black bg-white" /> Now</span>
          <div className="flex items-center border ml-2">
            <button
              onClick={zoomOut}
              disabled={effectiveZoom <= ZOOM_MIN}
              data-testid="timeline-zoom-out"
              aria-label="Zoom out"
              className="w-7 h-6 flex items-center justify-center border-r hover:bg-black hover:text-white disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-muted-foreground transition-colors"
            >
              <ZoomOut className="w-3 h-3" />
            </button>
            <button
              onClick={zoomReset}
              data-testid="timeline-zoom-reset"
              aria-label="Reset zoom"
              className="w-10 h-6 flex items-center justify-center border-r hover:bg-black hover:text-white transition-colors font-mono text-[10px]"
              title="Reset zoom"
            >
              {effectiveZoom}×
            </button>
            <button
              onClick={zoomIn}
              disabled={effectiveZoom >= ZOOM_MAX}
              data-testid="timeline-zoom-in"
              aria-label="Zoom in"
              className="w-7 h-6 flex items-center justify-center hover:bg-black hover:text-white disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-muted-foreground transition-colors"
            >
              <ZoomIn className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto overflow-y-visible" data-testid="timeline-scroller">
        <div
          className="relative px-4 pt-24 pb-24"
          style={{ width: effectiveZoom === 1 ? "100%" : `${effectiveZoom * 100}%`, minWidth: "100%" }}
        >
        {/* Rail */}
        <div className="absolute left-4 right-4 top-[110px] h-[2px] bg-neutral-300 dark:bg-neutral-700" />

        {/* Now marker at the far right */}
        <div className="absolute" style={{ left: `calc(${nowPos * 100}% )`, top: "102px" }}>
          <div className="w-3 h-3 border-2 border-black bg-white -translate-x-1/2" />
          <div className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground mt-2 -translate-x-1/2">Now</div>
        </div>

        {positioned.map((v) => {
          const isAuto = v.kind === "auto_pre_revision";
          const isSelected = selectedId === v.id;
          const isHovered = hoverId === v.id;
          const active = isSelected || isHovered;
          const label = truncate(v.reason || "Version", 42);
          // Alternate labels above/below to reduce collisions
          const labelAbove = v._idx % 2 === 0;
          return (
            <div
              key={v.id}
              className="absolute pointer-events-none"
              style={{ left: `${v._pos * 100}%`, top: "80px" }}
              data-testid={`timeline-node-${v._idx}`}
            >
              {/* Version tag chip above rail */}
              <div className="-translate-x-1/2 mb-2 px-1.5 py-0.5 border font-mono text-[9px] uppercase tracking-wider whitespace-nowrap w-fit"
                   style={{
                     background: active ? (isAuto ? "var(--peak-blue)" : "#111") : "#fff",
                     color: active ? "#fff" : (isAuto ? "var(--peak-blue)" : "#111"),
                     borderColor: isAuto ? "var(--peak-blue)" : "#111",
                   }}>
                v{v._idx + 1}
              </div>

              {/* Node dot — the ONLY clickable element (rest is pointer-events-none) */}
              <button
                type="button"
                onMouseEnter={() => setHoverId(v.id)}
                onMouseLeave={() => setHoverId(null)}
                onClick={() => onSelect?.(v)}
                data-testid={`timeline-dot-${v._idx}`}
                aria-label={`Version ${v._idx + 1}: ${v.reason || ""}`}
                className={`pointer-events-auto w-4 h-4 -translate-x-1/2 border-2 transition-transform duration-150 flex items-center justify-center cursor-pointer ${active ? "scale-150" : ""}`}
                style={{
                  background: isAuto ? "var(--peak-blue)" : "#111",
                  borderColor: isAuto ? "var(--peak-blue)" : "#111",
                }}
              >
                {isAuto && <Zap className="w-2.5 h-2.5 text-white" />}
              </button>

              {/* Label — either above chip (top) or below rail (bottom) */}
              <div
                className={`absolute -translate-x-1/2 w-40 text-center ${labelAbove ? "bottom-[calc(100%+4px)]" : "top-[26px]"}`}
              >
                <div className={`text-[10px] font-mono uppercase tracking-wider mb-0.5 ${isAuto ? "text-[var(--peak-blue)]" : "text-neutral-700 dark:text-neutral-400"}`}>
                  {isAuto ? "AI" : "Manual"}
                </div>
                <div className={`text-[11px] leading-tight ${active ? "font-medium text-foreground" : "text-muted-foreground"}`}>
                  {label}
                </div>
                <div className="text-[9px] font-mono text-muted-foreground mt-1">
                  {new Date(v.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>

              {/* Hover tooltip */}
              {isHovered && (
                <div className="pointer-events-none absolute z-20 bottom-[calc(100%+40px)] left-1/2 -translate-x-1/2 w-72 border bg-card shadow-lg p-3 text-left" data-testid={`timeline-tooltip-${v._idx}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {isAuto
                      ? <Zap className="w-3.5 h-3.5 text-[var(--peak-blue)]" />
                      : <User className="w-3.5 h-3.5" />}
                    <span className="text-[10px] font-mono uppercase tracking-wider">{isAuto ? "AI Revision" : "Manual Snapshot"}</span>
                    <span className="ml-auto font-mono text-[10px] text-muted-foreground">v{v._idx + 1}</span>
                  </div>
                  <div className="text-xs leading-relaxed">{v.reason || "—"}</div>
                  {v.doc_type_changed && (
                    <div className="mt-2 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                      Changed → {v.doc_type_changed}
                    </div>
                  )}
                  <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] font-mono border-t pt-2">
                    <div><span className="text-muted-foreground">Price:</span> ${(v.estimate?.external_price || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                    <div><span className="text-muted-foreground">Hours:</span> {v.estimate?.total_hours ?? "—"}h</div>
                  </div>
                  <div className="mt-2 pt-2 border-t text-[10px] font-mono uppercase tracking-wider text-[var(--peak-blue)]">
                    Click to view diff →
                  </div>
                </div>
              )}
            </div>
          );
        })}
        </div>
      </div>
    </div>
  );
}

function truncate(s, n) {
  if (!s) return "";
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}
